import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  OnInit,
  signal,
} from '@angular/core';
import { AiFeaturesService, AIStore } from '@maximizer/ai/data-access';
import {
  ContextService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { switchMap, tap } from 'rxjs';
import { IQBoostInternalCommand } from '../classes/iqboost';

@Component({
  selector: 'ai-iqboost-launcher',
  template: `
    <div
      kendoTooltip
      [popupTarget]="element"
      [tooltipTemplate]="aiDisabledTooltip"
      [showOn]="this.isEnabled() ? 'none' : 'hover'"
      [callout]="false"
      filter=".ai-button"
      class="flex"
    >
      @if (isLoaded() &&showIQBoost()) {
        <div class="ml-4 ai-button">
          <button
            kendoButton
            rounded="full"
            class="bg-primary-80 border-none h-8.5 px-4 gap-2 text-primary-200 hover:bg-primary-100"
            [disabled]="!this.isEnabled()"
            (click)="launchIqboost()"
          >
            <span
              class="maximizer-icons icon-solid icon-20 icon-iq-boost"
            ></span>
            <span class="launcher-label text-button whitespace-nowrap">
              {{ 'iqboost.buttons.askIqBoost' | translate }}
            </span>
          </button>
        </div>
      }
    </div>
    <ng-template #aiDisabledTooltip>
      <p
        class="text-caption whitespace-pre-wrap"
        [innerHTML]="
          disabledTooltipKey()
            | translate
            | regexReplace: '\\\\r\\\\n' : '\r\n'
        "
      ></p>
    </ng-template>
  `,
  // Scoped here rather than as Tailwind utilities: the launcher is mounted in the
  // legacy header, where only whatever utilities the host page's CSS already has apply.
  styles: `
    .launcher-label {
      position: relative;
      top: -1px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [KENDO_BUTTON, KENDO_TOOLTIP, UI_KENDO, UI_PIPES, TranslatePipe],
})
export class IqboostLauncherComponent
  extends EmbeddedComponent
  implements OnInit
{
  @HostBinding('attr.id') id = 'ai-iqboost-launcher';
  @HostBinding('class') aiClass = 'ai-ng';

  isEnabled = signal(false);
  isLoaded = signal(false);
  showIQBoost = computed(() => this.aiFeaturesService.showIQBoost());

  constructor(
    element: ElementRef,
    private readonly context: ContextService,
    public readonly aiStore: AIStore,
    private readonly session: SessionService,
    private readonly aiFeaturesService: AiFeaturesService,
  ) {
    super(element);
  }

  protected disabledTooltipKey(): string {
    return this.aiFeaturesService.isIQBoostV2Enabled()
      ? 'ai.disabled'
      : 'ai.disabledClassic';
  }

  ngOnInit(): void {
    this.session.getInfo()
      .pipe(
        switchMap((session) => this.aiFeaturesService.getConfiguration(session)),
        tap((config) => {
          this.aiStore.setUserDataGroup(config.aiUserDataGroup);
          this.isEnabled.set(config.enabled);
        }),
        tap(() => this.isLoaded.set(true)),
      )
      .subscribe();
  }

  override doAction(name: string, args?: unknown): void {
      if (name === IQBoostInternalCommand.SetEnabled) {
      this.isEnabled.set(args as boolean);
    }
  }

  launchIqboost(): void {
    this.context.showComponentDialog('maximizer-ai-iqboost', {
      key: '',
      type: '',
    });
  }
}
